import { useState } from 'react';
import { useRouter } from 'next/router';

import { useTrack } from '@blockworks/platform/services/analytics';
import { Bit } from '@blockworks/ui/bit';
import { FlexBox, IconButton, Link, Logo, Text } from '@blockworks/ui/components';
import { XCloseIcon } from '@blockworks/ui/icon';

import { AccountSignInForm } from '@/components/auth/account-sign-in-form';
import { AnalyticsEvent } from '@/models/analytics';
import { AppRoutes } from '@/services/auth/auth.routes';

type SignInSectionProps = {
    subtitle?: string;
    full?: boolean;
};

const getContainerStyles = (full: boolean) => (full ? ({ h: 'full', w: 'full' } as const) : ({ mt: 6 } as const));

const SignInContent = ({ subtitle, handleSignUpClick }: { subtitle: string; handleSignUpClick: () => void }) => (
    <FlexBox col gap="xl" m={5} p={6} center>
        <Logo />
        <FlexBox col gap="md" pr={5} center>
            <Text size="display-sm" weight="medium" center>
                Unlock crypto&#8217;s most powerful research platform.
            </Text>
            <Text color="deselect" size="md" center>
                {subtitle}
            </Text>
        </FlexBox>
        <div className="w-[300px] flex flex-col gap-6">
            <AccountSignInForm>
                <FlexBox gap="xs" center w="full">
                    <Text size="sm" color="muted">
                        {`Don't have an account?`}
                    </Text>
                    <Link
                        intent="primary"
                        size="sm"
                        fontWeight="medium"
                        onClick={handleSignUpClick}
                        href={AppRoutes.SIGN_UP.pathname}
                    >
                        Sign up
                    </Link>
                </FlexBox>
            </AccountSignInForm>
        </div>
    </FlexBox>
);

const SignInSection = ({
    full = false,
    subtitle = 'Sign up for Blockworks Research to access insights you can’t invest without.',
}: SignInSectionProps) => {
    const [hideSection, setHideSection] = useState(false);
    const router = useRouter();
    const track = useTrack();

    const handleSignUpClick = () => {
        const event = AnalyticsEvent.signupFromUnlockedContent;
        track(event, { source: router.asPath });
    };

    if (hideSection) return null;

    return (
        <Bit.div bgColor="base" p={6} {...getContainerStyles(full)} pos="relative" bottom={0} w="full">
            <span className="absolute right-2">
                <IconButton
                    aria-label="Close"
                    size="sm"
                    icon={XCloseIcon}
                    borderRadius="full"
                    onClick={() => setHideSection(true)}
                />
            </span>
            <SignInContent subtitle={subtitle} handleSignUpClick={handleSignUpClick} />
        </Bit.div>
    );
};

export default SignInSection;
